import * as React from "react";
import { v4 as uuidv4 } from "uuid";
import { formInitialize, formSet, formUninitialize, useCpFormSubmit } from "../gvc/testNewForm/utils";
import { useAppDispatch } from "../../store/hooks";
import { formActions } from "../../store/formSlice";
import { Modal, notification } from "antd";
import { linksOf, request } from "../../services/cpln";
import { CpFormPromptDetail } from "../../components/antd/form/CpFormPromptDetail";
import { CpFormContext } from "../../components/form/CpFormContext";
import { CpFormValidator } from "../gvc/testNewForm/CpFormValidator";
import { CpPreviewModal } from "../workload/draft/previewModal";
import { CpDebugModal } from "../workload/draft/debugModal";
import { ServiceAccount } from "../../schema/types/serviceAccount/entity";
import { inputToServiceAccount, serviceAccountToInput, serviceAccountToPatch } from "./utils";
import { useGetServiceAccountQuery } from "../../store/api/serviceAccountApi";
import { getValidator } from "./create/validator";
import { ServiceAccountInput } from "../../schema/types/serviceAccount/input";
import { ServiceAccountDetailContent } from "./detailContent";
import { getDirtyChecker } from "./create/dirtyChecker";
import { CpFormDirtyChecker } from "../gvc/testNewForm/CpFormDirtyChecker";

interface Props {
  serviceAccount: ServiceAccount;
}
export const ServiceAccountDetailForm: React.FC<Props> = ({ serviceAccount }) => {
  const dispatch = useAppDispatch();
  const formIdRef = React.useRef(uuidv4());
  const formId = formIdRef.current;

  const { refetch } = useGetServiceAccountQuery(linksOf(serviceAccount).self!);

  // form init
  const [isInitialized, setIsInitialized] = React.useState<boolean>(false);
  React.useEffect(() => {
    const input = serviceAccountToInput(serviceAccount);
    formInitialize(formId, input);
    setIsInitialized(true);
    return () => {
      formUninitialize(formId);
    };
  }, []);

  // confirm
  React.useEffect(() => {
    dispatch(formActions.setInitialValues({ id: formId, values: serviceAccountToInput(serviceAccount) }));
  }, [serviceAccount.version]);

  // validation/dirty/submit
  const validatorRef = React.useRef(getValidator(formId));
  const validator = validatorRef.current;
  const dirtyCheckerRef = React.useRef(getDirtyChecker(formId));
  const dirtyChecker = dirtyCheckerRef.current;
  const submit = useCpFormSubmit({ id: formId, validationFn: validator, onSubmit: handleSubmit });

  async function handleSubmit(input: ServiceAccountInput) {
    try {
      let body = serviceAccountToPatch(inputToServiceAccount(input), { id: input.id, version: input.version });
      const method = "patch";
      const url = linksOf(serviceAccount).self!;

      await request({
        method: method,
        url: url,
        body: body,
      });

      formSet(formId, `meta.applied`, true);

      notification.success({
        message: "Success",
        description: "Service Account is updated",
      });

      refetch();
    } catch (e) {
      if (e?.response?.status === 409) {
        Modal.warning({
          title: "Update Conflict",
          content: "This resource has been modified since you opened it. The form will be refreshed with the latest data and your changes will be lost.",
          onOk: () => {
            refetch();
          },
        });
        return;
      }

      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    }
  }

  if (!isInitialized) {
    return null;
  }

  return (
    <CpFormContext.Provider value={{ id: formId, submit: submit }}>
      <ServiceAccountDetailContent serviceAccount={serviceAccount} refetch={refetch} />
      <CpFormValidator validationFn={validator} />
      <CpFormDirtyChecker checker={dirtyChecker} />
      <CpFormPromptDetail link={linksOf(serviceAccount).self!} inputToItemFn={inputToServiceAccount} />
      <CpPreviewModal
        inputToItemFn={inputToServiceAccount}
        kind="serviceaccount"
        namePath="name"
        title="Service Account"
      />
      <CpDebugModal
        inputToItemFn={inputToServiceAccount}
        kind="serviceaccount"
        namePath="name"
        title="Service Account"
      />
    </CpFormContext.Provider>
  );
};
